import { useState, useEffect, useRef } from 'react';

export default function CarCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('All');
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const categories = [
    'All',
    'Citadine',
    'Berline',
    'SUV',
    'Familiale',
    'Sportive',
    'Hybride'
  ];

  const allCars = [
    {
      name: 'Peugeot 208',
      price: '300',
      image: '/assets/Cars/208.png',
      features: ['5 places', 'Diesel', 'Climatisation'],
      categories: ['Citadine']
    },
    {
      name: 'Dacia Logan',
      price: '250',
      image: '/assets/Cars/dacia logan.png',
      features: ['5 places', 'Diesel', 'Économique'],
      categories: ['Berline']
    },
    {
      name: 'Dacia Sandero',
      price: '250',
      image: '/assets/Cars/dacia sandero.png',
      features: ['5 places', 'Essence', 'Confort'],
      categories: ['Citadine']
    },
    {
      name: 'Opel Corsa',
      price: '300',
      image: '/assets/Cars/opel corsa.png',
      features: ['5 places', 'Manuelle', 'Bluetooth'],
      categories: ['Citadine']
    },
    {
      name: 'Peugeot 208 Hybrid',
      price: '350',
      image: '/assets/Cars/208 hybrid.png',
      features: ['5 places', 'Hybride', 'Automatique'],
      categories: ['Citadine', 'Hybride']
    },
    {
      name: 'Dacia Logan Nouv',
      price: '300',
      image: '/assets/Cars/dacia sandero grey.png',
      features: ['5 places', 'Diesel', 'Grand coffre'],
      categories: ['Berline']
    },
    {
      name: 'Hyundai Tucson Hybrid',
      price: '800',
      image: '/assets/Cars/tucson hybrid.png',
      features: ['5 places', 'Hybride', 'Automatique'],
      categories: ['SUV', 'Hybride']
    },
    {
      name: 'Cupra Leon',
      price: '700',
      image: '/assets/Cars/cupra leon.png',
      features: ['5 places', 'Automatique', 'Sportive'],
      categories: ['Sportive']
    },
    {
      name: 'Dacia Jogger',
      price: '400',
      image: '/assets/Cars/dacia jogger.png',
      features: ['7 places', 'Diesel', 'Familiale'],
      categories: ['Familiale']
    },
    {
      name: 'Kia Sportage',
      price: '600',
      image: '/assets/Cars/kia sportage.png',
      features: ['5 places', 'Diesel', 'Confort'],
      categories: ['SUV']
    }
  ];

  const filteredCars = activeCategory === 'All'
    ? allCars
    : allCars.filter(car => car.categories.includes(activeCategory));

  const getSlidesPerView = () => {
    if (typeof window === 'undefined') return 1;
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 640) return 2;
    return 1;
  };

  const [slidesPerView, setSlidesPerView] = useState(getSlidesPerView());

  useEffect(() => {
    const handleResize = () => {
      setSlidesPerView(getSlidesPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset index when category changes
  useEffect(() => {
    setCurrentIndex(0);
    setIsAutoPlaying(true);
  }, [activeCategory]);

  const maxIndex = Math.max(0, filteredCars.length - slidesPerView);

  useEffect(() => {
    if (!isAutoPlaying || maxIndex === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, maxIndex]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsDragging(true);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext(); // Swipe Left -> Next
      } else {
        handlePrev(); // Swipe Right -> Prev
      }
    }
    setIsDragging(false);
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  return (
    <section className="relative py-16 sm:py-20 bg-gradient-to-b from-black via-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Notre Flotte Premium
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-8">
            Des véhicules modernes et bien entretenus pour tous vos besoins
          </p>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 overflow-x-auto pb-4 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm sm:text-base font-medium transition-all duration-300 whitespace-nowrap ${activeCategory === category
                    ? 'bg-[#fe7222] text-white shadow-lg shadow-[#fe7222]/30 scale-105'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="relative w-full">
          {/* Desktop Navigation Buttons - Only show if enough items to scroll */}
          {filteredCars.length > slidesPerView && (
            <>
              <button
                onClick={handlePrev}
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-[#fe7222] transition-colors border border-white/20 text-white"
              >
                <i className="ri-arrow-left-s-line text-2xl"></i>
              </button>

              <button
                onClick={handleNext}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 z-20 w-12 h-12 items-center justify-center rounded-full bg-white/10 hover:bg-[#fe7222] transition-colors border border-white/20 text-white"
              >
                <i className="ri-arrow-right-s-line text-2xl"></i>
              </button>
            </>
          )}

          <div
            className="overflow-hidden w-full max-w-full"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            ref={carouselRef}
          >
            {filteredCars.length > 0 ? (
              <div
                className="flex transition-transform duration-500 ease-out will-change-transform"
                style={{
                  transform: `translateX(-${currentIndex * (100 / slidesPerView)}%)`,
                }}
              >
                {filteredCars.map((car, index) => (
                  <div
                    key={`${car.name}-${index}`}
                    className="flex-shrink-0 px-3"
                    style={{ width: `${100 / slidesPerView}%` }}
                  >
                    <div className="group relative backdrop-blur-xl bg-white/5 border border-white/10 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(254,114,34,0.15)] hover:border-[#fe7222]/30 cursor-pointer h-full">

                      {/* Background Title - Decorative */}
                      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-5xl sm:text-6xl md:text-7xl font-bold text-white/[0.03] whitespace-nowrap pointer-events-none select-none z-0">
                        {car.name.split(' ')[0]}
                      </div>

                      {/* Image Container - STRICT FIXED SIZE & ALIGNMENT */}
                      <div className="relative w-full h-48 sm:h-56 md:h-64 flex items-center justify-center p-6 z-10">
                        <img
                          src={car.image}
                          alt={car.name}
                          className="max-w-full max-h-full w-auto h-auto object-contain drop-shadow-xl"
                          loading="lazy"
                          draggable="false"
                        />
                      </div>

                      {/* Car Info */}
                      <div className="relative z-10 p-5 sm:p-6 bg-gradient-to-t from-black/80 to-transparent">
                        <h3 className="text-xl sm:text-2xl font-bold mb-3 text-white">{car.name}</h3>

                        {/* Features */}
                        <div className="flex flex-wrap gap-2 mb-5">
                          {car.features.map((feature, idx) => (
                            <span
                              key={idx}
                              className="text-xs text-gray-300 px-3 py-1 bg-white/5 border border-white/10 rounded-full"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>

                        {/* Price & Action */}
                        <div className="flex items-center justify-between mt-auto">
                          <div className="flex flex-col">
                            <span className="text-2xl sm:text-3xl font-bold text-[#fe7222]">{car.price} DH</span>
                            <span className="text-xs text-gray-500">par jour</span>
                          </div>
                          <a
                            href="https://wa.me/212667304231"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#fe7222]/10 border border-[#fe7222]/30 rounded-full hover:bg-[#fe7222] hover:border-[#fe7222] transition-all duration-300 group-hover:scale-105"
                            aria-label={`Réserver ${car.name}`}
                          >
                            <i className="ri-whatsapp-line text-lg sm:text-xl text-[#fe7222] group-hover:text-white transition-colors"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                Aucun véhicule disponible dans cette catégorie pour le moment.
              </div>
            )}
          </div>

          {/* Pagination Content - Only show if enough items to scroll */}
          {filteredCars.length > slidesPerView && maxIndex > 0 && (
            <div className="flex justify-center gap-2 mt-8 sm:mt-12">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                    setTimeout(() => setIsAutoPlaying(true), 5000);
                  }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${index === currentIndex
                    ? 'w-8 bg-[#fe7222]'
                    : 'w-2 bg-white/20 hover:bg-white/40'
                    }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
